/**
 * Ведение справочников (ТЗ FR-43).
 *
 * Записи не удаляются, а архивируются: архивная запись перестаёт предлагаться
 * в новых делах, но ранее сформированные документы остаются воспроизводимыми
 * (ТЗ п. 5.3).
 */

import { useEffect, useState } from 'react';
import { api } from '../../api.js';

const DICTIONARIES: Array<[name: string, title: string, fields: Array<[key: string, label: string]>]> = [
  ['courts', 'Арбитражные суды', [['regionCode', 'Код субъекта'], ['regionName', 'Субъект РФ'], ['address', 'Почтовый адрес']]],
  ['sro', 'СРО арбитражных управляющих', [['fullName', 'Полное наименование'], ['fullNameGenitive', 'Полное наименование в родительном падеже'], ['ogrn', 'ОГРН'], ['inn', 'ИНН'], ['legalAddress', 'Юридический адрес'], ['actualAddress', 'Фактический адрес'], ['postalAddress', 'Почтовый адрес']]],
  ['creditors', 'Кредитные организации', [['inn', 'ИНН'], ['ogrn', 'ОГРН'], ['address', 'Адрес']]],
  ['credit-bureaus', 'Бюро кредитных историй', []],
  ['obligation-types', 'Содержание обязательства', []],
  ['obligation-bases', 'Основания возникновения', []],
  ['confirming-documents', 'Подтверждающие документы', []],
  ['deal-kinds', 'Виды сделок', []],
  ['appendix-items', 'Пункты перечня приложений', [['key', 'Ключ'], ['text', 'Текст пункта'], ['condition', 'Условие включения']]],
];

type Entry = Record<string, unknown> & { id: string; name: string; archived?: boolean };

export function DictionariesPage() {
  const [name, setName] = useState('courts');
  const [entries, setEntries] = useState<Entry[]>([]);
  const [draft, setDraft] = useState<Entry | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const definition = DICTIONARIES.find(([value]) => value === name)!;

  const load = () => {
    api
      .dictionary(name, { limit: 500 })
      .then((r) => setEntries(r.entries as Entry[]))
      .catch((e) => setMessage((e as Error).message));
  };

  useEffect(load, [name]);

  const save = async () => {
    if (!draft) return;
    try {
      await api.saveDictionaryEntry(name, draft);
      setDraft(null);
      setMessage('Запись сохранена');
      load();
    } catch (error) {
      setMessage((error as Error).message);
    }
  };

  const archive = async (entry: Entry) => {
    try {
      await api.archiveDictionaryEntry(name, entry.id, !entry.archived);
      load();
    } catch (error) {
      setMessage((error as Error).message);
    }
  };

  return (
    <section className="block">
      <h2>Справочники</h2>

      <div className="toolbar">
        <select value={name} onChange={(e) => setName(e.target.value)}>
          {DICTIONARIES.map(([value, title]) => (
            <option key={value} value={value}>
              {title}
            </option>
          ))}
        </select>
        <button type="button" className="link-button" onClick={() => setDraft({ id: '', name: '' })}>
          + Добавить запись
        </button>
        {message && <span className="hint">{message}</span>}
      </div>

      {name === 'appendix-items' && (
        <p className="hint">
          Условие включения — имя признака из правил ветвления, например <code>is_married</code>,{' '}
          <code>has_children</code>, <code>!is_employed</code> или <code>is_married|is_divorced</code>.
          Пустое условие означает безусловный пункт.
        </p>
      )}

      {draft && (
        <div className="draft">
          <label className="field">
            <span className="field-label">Наименование</span>
            <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          </label>

          {definition[2].map(([key, label]) => (
            <label className="field" key={key}>
              <span className="field-label">{label}</span>
              <input value={(draft[key] as string) ?? ''} onChange={(e) => setDraft({ ...draft, [key]: e.target.value })} />
            </label>
          ))}

          <div className="toolbar">
            <button type="button" className="primary" onClick={save}>
              Сохранить
            </button>
            <button type="button" className="link-button" onClick={() => setDraft(null)}>
              Отмена
            </button>
          </div>
        </div>
      )}

      <table className="simple-table">
        <thead>
          <tr>
            <th>Наименование</th>
            {definition[2].map(([key, label]) => (
              <th key={key}>{label}</th>
            ))}
            <th />
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => (
            <tr key={entry.id} className={entry.archived ? 'archived' : undefined}>
              <td>{entry.name}</td>
              {definition[2].map(([key]) => (
                <td key={key}>{String(entry[key] ?? '')}</td>
              ))}
              <td>
                <button type="button" className="link-button" onClick={() => setDraft(entry)}>
                  Изменить
                </button>{' '}
                <button type="button" className="link-button" onClick={() => archive(entry)}>
                  {entry.archived ? 'Вернуть' : 'В архив'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
